// Stale-tab session watcher.
//
// A tab left open past the session's idle lifetime fails in ugly ways when
// the user comes back: a classic form post 419s into a login redirect and
// Livewire falls back to a native confirm() dialog. This module runs on every
// page that loads app.js — the guest layout, the app layout, the visual tool
// and the 403 error layout (errors/custom.blade.php). pdf/report.blade.php loads
// it too, but only the PDF-export function ever renders that, with no session
// and no form, so nothing below applies there:
//
//  - When the tab regains visibility/focus, ping /_session-ping. If the live
//    session still belongs to the SAME account the page was rendered for
//    (meta[name="aaa-user"] vs the ping's user) and the CSRF token rotated,
//    write the fresh one into meta[name="csrf-token"] and every form's _token
//    input — the next submit just works. Livewire re-reads the meta tag on
//    every request, so the same write heals it too.
//  - On an identity change (session expired/logged out, or a DIFFERENT account
//    signed in from another tab), never heal — healing would re-point this
//    page's forms at the other account. Open the session-expired modal instead.
//    See components/modal/session-expired.blade.php.
//  - A Livewire request that lands on a 419 opens the same modal in place of
//    Livewire's native confirm(). The Handler answers Livewire requests with a
//    real JSON 419 (not the usual redirect) precisely so this hook can see it.
//  - A classic form that submits WHILE a ping is still out is held back until
//    the ping lands, then submitted again with the healed token. Without that,
//    the click that brings the tab back into focus and the click that submits
//    the form are the same click, and the POST leaves with the dead token.
//
// Every layout a user's tab can load this bundle from must carry the
// meta[name="aaa-auth"], meta[name="aaa-user"] and meta[name="csrf-token"] tags,
// mount <x-modal.session-expired />, and include @livewireScripts (this bundle
// never imports Alpine; Livewire's script is what boots it, so without that the
// modal is in the DOM and inert). Without the metas a signed-in user's page reads
// as a guest page here, the identity gate below refuses every ping — a healthy
// session included — and a held submit is dropped; without the modal that
// refusal has nowhere to show. The 403 layout shipped with none of it, and its
// Request Access click vanished in silence.

const ENDPOINT = '/_session-ping';
const MIN_PING_INTERVAL_MS = 60000;
// Longest a submit is ever held waiting for a ping. A ping normally lands in a
// few hundred ms; this only matters when the network has gone away.
const DEFER_SUBMIT_TIMEOUT_MS = 3000;
// The heal rewrites these, and their presence is what makes a form worth holding.
// One constant so the two uses can never drift apart.
const TOKEN_INPUT_SELECTOR = 'input[name="_token"]';
// A held submit under this is not worth announcing — a ping normally lands in about
// 250ms, and talking over a submit already on its way is worse than saying nothing.
// Past it the page is silent and unchanged while the user waits, so say so.
const HOLD_ANNOUNCE_AFTER_MS = 400;

let lastPingAt = 0;
let pingModalShown = false; // proactive pings nag at most once per page life
let inFlightPing = null;    // set only while a ping is actually out (see deferred submits)

function pageWasAuthenticated() {
    return document.querySelector('meta[name="aaa-auth"]')?.content === '1';
}

// The user id this page was rendered for; '' on guest pages.
function pageRenderedUser() {
    return document.querySelector('meta[name="aaa-user"]')?.content ?? '';
}

function hasSessionModal() {
    return !!document.getElementById('session-expired-modal');
}

function currentPageToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
}

function applyFreshToken(token) {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) meta.content = token;

    document.querySelectorAll(TOKEN_INPUT_SELECTOR).forEach((input) => {
        input.value = token;
    });
}

// bfcache restores the DOM verbatim, hold markers and all. A page left mid-hold (the
// user clicked a link during the wait, then came back) would return carrying them, and
// the pageshow handler below puts a ping in flight straight away — so a stale
// data-aaa-heal-held would make the listener swallow the next submit forever (a dead
// button), and a stale data-aaa-heal-deferred would make it skip the hold. Neither
// marker is ever meaningful across a restore, so drop both.
function clearHoldMarkers() {
    document.querySelectorAll('[data-aaa-heal-held], [data-aaa-heal-deferred]').forEach((form) => {
        delete form.dataset.aaaHealHeld;
        delete form.dataset.aaaHealDeferred;
    });
}

// A form the submit listener is holding right now (see below). Read from the DOM rather
// than a flag so the two can never disagree: the marker is what the listener itself keys on.
function submitIsHeld() {
    return !!document.querySelector('form[data-aaa-heal-held="true"]');
}

// AbortSignal.timeout() only reached browsers in 2022 (Safari 16, Chrome 103, Firefox
// 100). Called unguarded inside the ping's try it would throw before the request left,
// the catch there would swallow it, and the heal would be silently dead on exactly the
// browsers least likely to be updated — a regression, not a degraded mode. Older
// browsers get the same bound through a hand-rolled controller; only where even that is
// missing does the ping go out unbounded, which is what it did before the cap existed.
function timeoutSignal(ms) {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
        return AbortSignal.timeout(ms);
    }
    if (typeof AbortController === 'function') {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), ms);
        return controller.signal;
    }
    return undefined;
}

function openModal(mode, { force = false } = {}) {
    if (!force) {
        if (pingModalShown) return;
        pingModalShown = true;
    }

    window.dispatchEvent(new CustomEvent('open-session-expired', { detail: { mode } }));
}

// reactTo419: this ping follows a 419 we already intercepted, so it bypasses
// the throttle and always ends by opening the modal — the native confirm was
// suppressed, and leaving the user in silence is worse than the throttle.
// Publishes the request as `inFlightPing` for the whole time it is out, so a
// submit fired mid-ping can wait for it. The throttled return is handled here
// rather than inside runSessionCheck: nothing goes out on that path, so a
// submit must not be held for a ping that never runs.
async function checkSession(options = {}) {
    const { reactTo419 = false } = options;
    const now = Date.now();
    if (!reactTo419 && now - lastPingAt < MIN_PING_INTERVAL_MS) return;
    lastPingAt = now;

    const ping = runSessionCheck(options);
    inFlightPing = ping;

    try {
        return await ping;
    } catch (_) {
        // A watcher must never break the page, and a held submit is waiting on
        // this promise — swallowing here keeps both the page and the submit alive.
    } finally {
        if (inFlightPing === ping) inFlightPing = null;
    }
}

async function runSessionCheck({ reactTo419 = false } = {}) {
    let state;
    try {
        const response = await fetch(ENDPOINT, {
            headers: { 'Accept': 'application/json' },
            credentials: 'same-origin',
            // Freshness endpoint: never serve a cached (stale-token) copy, which
            // would heal the page with the wrong token and 419 the next submit.
            cache: 'no-store',
            // Bound the request. A stalled connection that never errors (captive
            // portal, Wi-Fi still down on wake) would otherwise keep inFlightPing
            // set for the browser's own network timeout — minutes — during which
            // every classic form submit pays the full hold and then posts stale,
            // and the catch below never runs so no later focus retries.
            signal: timeoutSignal(DEFER_SUBMIT_TIMEOUT_MS),
        });
        if (!response.ok) throw new Error('session ping failed');
        state = await response.json();
    } catch (_) {
        // Offline or blocked — a watcher must never break the page. Do NOT
        // hold the throttle: the user is back at the tab and the next focus
        // event should retry immediately (e.g. Wi-Fi still reconnecting on wake).
        lastPingAt = 0;
        if (reactTo419) openModal(pageWasAuthenticated() ? 'logged-out' : 'expired', { force: true });
        // A dead ping still releases a held submit: it lands on today's behaviour
        // (a 419 into the server-side backstop), which beats a dead button.
        return { healed: true };
    }

    // Identity gate, BEFORE any heal. A logout+login in another tab rotates
    // the token exactly like an expiry (Fortify regenerates the session on
    // every login), so the token alone can't tell "same user, fresh token"
    // from "someone else is signed in now". Healing across accounts would
    // re-point this page's forms at the other account: submits would run as
    // them and be attributed to them. Same user (or guest page + guest
    // session, '' === '') falls through and heals as before.
    const liveUser = state.user == null ? '' : String(state.user);
    if (liveUser !== pageRenderedUser()) {
        // Live session signed out -> offer Log in. Live session is a
        // different account (or the page went guest->authed) -> the page no
        // longer matches the session; offer a refresh.
        //
        // The once-per-page budget in openModal() is for proactive pings the user never
        // asked for. A held submit is the user actively trying to do something, and this
        // arm is about to keep it (below): if the modal stayed closed because it was shown
        // earlier in this page's life, their click would vanish into silence — a working
        // control indistinguishable from a broken one. So a held submit always forces it.
        openModal(state.authenticated ? 'expired' : 'logged-out', { force: reactTo419 || submitIsHeld() });
        // Deliberately NOT healed. A held submit must stay held: posting the token
        // we just refused to replace is a guaranteed 419, and the navigation would
        // throw away the modal opened a microsecond ago.
        return { healed: false };
    }

    if (state.csrf_token && state.csrf_token !== currentPageToken()) {
        applyFreshToken(state.csrf_token);
    }

    if (reactTo419) {
        // Same user and the token is healed, but the failed request left this
        // page's component state stale — a refresh is the honest next step.
        openModal('expired', { force: true });
    }

    return { healed: true };
}

// Hold a form that submits mid-ping, then submit it again once the token is healed.
//
// Capture phase on `document`, so this runs before anything on the form itself — and a
// held submit stops right here: propagation is stopped along with the default, so the
// form's own handlers never see it. Those handlers are where forms flip into a
// "submitting" state (the invitation, login and logout forms disable their button on
// submit), and that state has to track the POST, not our wait. Let it flip on the held
// event and the identity-gate refusal below would leave a disabled button with nothing
// to re-enable it. The replay is a real submit event, so every handler runs then — once,
// at the moment the request actually leaves.
//
// Only classic forms are held. A Livewire form does not post the _token input
// (Livewire sends the meta token as a header) and already has the 419 backstop above,
// so holding it would only fight that. No _token input means nothing the heal
// rewrites, so nothing to wait for.
document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // This is the replay below re-firing. Clear the flag and let it through.
    //
    // Checked BEFORE the inFlightPing gate on purpose: by the time resubmit() runs,
    // checkSession's finally has already nulled inFlightPing (its await is queued on
    // the ping ahead of our race), so gating this behind it would leave
    // data-aaa-heal-deferred="true" on the form for good. bfcache restores the DOM
    // verbatim and the pageshow handler below re-pings, so a stale flag would make
    // the next submit on a restored page skip the hold entirely — the exact 419 this
    // whole listener exists to prevent.
    if (form.dataset.aaaHealDeferred === 'true') {
        delete form.dataset.aaaHealDeferred;
        return;
    }

    if (!inFlightPing) return;
    if ([...form.attributes].some((a) => a.name.startsWith('wire:submit'))) return;
    if (!form.querySelector(TOKEN_INPUT_SELECTOR)) return;

    // Already held. Swallow the extra activation rather than queue a second replay:
    // most classic @csrf forms show no submitting state, so a second click during
    // the wait is the expected user reaction, not a mistake.
    if (form.dataset.aaaHealHeld === 'true') {
        event.preventDefault();
        event.stopPropagation();
        return;
    }

    // form.submit() ignores the submitter, so a `formaction` button would post to
    // the form's own action instead — see auth/claim-workspace-confirm.blade.php,
    // where that turns "Discard scan" into "Add site to workspace". If we cannot
    // replay the submit faithfully, do not intercept it at all: a 419 into the
    // server-side backstop is recoverable, the wrong endpoint is not.
    if (typeof form.requestSubmit !== 'function') return;

    event.preventDefault();
    event.stopPropagation();
    form.dataset.aaaHealHeld = 'true';

    // Most classic @csrf forms flip nothing on submit, so a long hold looks exactly
    // like a dead button. Announce it politely, and only once it is noticeable.
    // The copy lives on the element so it stays translatable — WCAG 4.1.3.
    const status = document.getElementById('aaa-submit-status');
    const announce = setTimeout(() => {
        if (status) status.textContent = status.dataset.holdingMessage ?? '';
    }, HOLD_ANNOUNCE_AFTER_MS);
    const clearAnnouncement = () => {
        clearTimeout(announce);
        if (status) status.textContent = '';
    };

    const pendingPing = inFlightPing;
    // A disabled submitter contributes no name/value, so drop it — unless it carries
    // formaction, which is honoured regardless of disabled state and decides the
    // endpoint.
    const submitter = event.submitter;
    const replaySubmitter = submitter && (!submitter.disabled || submitter.hasAttribute('formaction'))
        ? submitter
        : undefined;

    const resubmit = () => {
        clearAnnouncement();
        delete form.dataset.aaaHealHeld;
        try {
            form.dataset.aaaHealDeferred = 'true';
            form.requestSubmit(replaySubmitter);
        } catch (error) {
            // requestSubmit() throws NotFoundError if the submitter stopped belonging
            // to this form while we held it (these forms sit inside Livewire
            // components, which morph). The user's click is already swallowed, so
            // retry without the submitter — but never when it carried formaction, or
            // the retry posts to the wrong endpoint.
            delete form.dataset.aaaHealDeferred;
            if (replaySubmitter && replaySubmitter.hasAttribute('formaction')) return;
            try {
                form.dataset.aaaHealDeferred = 'true';
                form.requestSubmit();
            } catch (_) {
                delete form.dataset.aaaHealDeferred;
            }
        }
    };

    // Both arms run: a ping that rejects must still let the form go, or the user is
    // stuck on a page whose only button does nothing. `undefined` means the cap fired
    // rather than the ping landing, so go anyway. `{healed: false}` is the identity
    // gate refusing to heal — drop the submit and leave the modal it forced open on
    // screen. Nothing on the form needs undoing: its handlers never saw the held event,
    // so the button is exactly as the user left it, and clearing the marker means the
    // next click is not swallowed as a repeat.
    Promise.race([
        pendingPing,
        new Promise((resolve) => { setTimeout(resolve, DEFER_SUBMIT_TIMEOUT_MS); }),
    ]).then((result) => {
        if (result && result.healed === false) {
            clearAnnouncement();
            delete form.dataset.aaaHealHeld;
            return;
        }
        resubmit();
    }, resubmit);
}, true);

document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkSession();
});
window.addEventListener('focus', () => checkSession());

// A page revived from the back/forward cache (or a browser-restart tab
// restore, which navigates as back_forward) carries the HTML — and token — it
// was first rendered with, however old that is. Check immediately.
window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        clearHoldMarkers();
        lastPingAt = 0;
        checkSession();
    }
});
if (performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward') {
    checkSession();
}

document.addEventListener('livewire:init', () => {
    if (!window.Livewire) return;

    window.Livewire.hook('request', ({ fail }) => {
        fail(({ status, preventDefault }) => {
            if (status !== 419) return;

            // No modal on this page (e.g. a standalone view that didn't mount
            // it) — leave Livewire's own "page expired, refresh?" confirm in
            // place rather than suppressing it into silence.
            if (!hasSessionModal()) return;

            preventDefault();

            // Ping first, then open the right modal from the true auth state.
            // The failed update is already lost; this also heals the token so
            // the user's next action starts from a working page.
            lastPingAt = 0;
            checkSession({ reactTo419: true });
        });
    });
});
